import React, { memo, useState, useEffect } from 'react';
import { Row, Badge, Spinner, Col, Card } from 'react-bootstrap';
import { getStillageAuditDetails } from 'services/stillageDashboard';
import PropTypes from 'prop-types';
import Avatar from 'components/common/Avatar';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faLongArrowAltRight } from '@fortawesome/free-solid-svg-icons';

const StillageAudit = ({ showModal, stillageNo }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!showModal || !stillageNo) {
            setData([]);
            setLoading(false);
            return;
        }

        // Captured locally so a superseded request can tell it is no longer the current one
        const controller = new AbortController();
        setLoading(true);

        getStillageAuditDetails(stillageNo, controller)
            .then(res => {
                if (controller.signal.aborted) return;
                setData(res ?? []);
            })
            .catch(error => {
                if (controller.signal.aborted) return;
                setData([]);
                toast.error(`Error fetching audit data: ${error.message}`);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [showModal, stillageNo]);

    if (loading)
        return (
            <div className='justify-content-center d-flex w-100 my-5'>
                <Spinner animation="border" role="status" variant='primary' />
            </div>
        );

    if (!data?.length && !loading)
        return (
            <div className='h-100 text-center align-middle my-4'>No data</div>
        );

    return (
        <Row className='p-3'>
            {
                data.map((change, idx) => {

                    return (
                        <Col xs={12} key={`${change.userId}_${idx}`} className='position-relative'>
                            <div
                                className='bg-400 h-100'
                                style={{
                                    top: 0, left: 15, position: 'absolute', width: '1px'
                                }}
                            ></div>
                            <div style={{ top: 0, left: 0, position: 'absolute', zIndex: 1 }}>
                                <Avatar name={change.userName} />
                            </div>
                            <Card bg='light' text='dark' className='mt-2 shadow-sm'>
                                <Card.Header>
                                    <div className='mx-4 d-flex justify-content-between'>
                                        <div><b>{change.userName}</b> changed a record</div>
                                        <small className='float-end'><b>{dayjs(change.changeDateTime).format('DD MMMM YYYY HH:mm:ss')}</b></small>
                                    </div>
                                </Card.Header>
                                <Card.Body className='mx-4 mb-3 p-2 bg-200 p-4 rounded'>
                                    <Row className='p-2'>
                                        <Col>
                                            <Badge bg='info'><FontAwesomeIcon icon={faBox} /> Stillage #{stillageNo}</Badge>
                                            {' '}
                                            <Badge bg='warning'>Field: <b className='text-uppercase'>{change.fieldName}</b></Badge>
                                        </Col>
                                    </Row>
                                    <Row className='p-2 d-flex align-items-center'>
                                        <Col xs={5}>
                                            <Badge bg='danger' className='w-100 shadow-sm text-decoration-line-through text-wrap'>{change.originalData || ''}</Badge>
                                        </Col>
                                        <Col xs={2} className='text-center fs-2'>
                                            <FontAwesomeIcon icon={faLongArrowAltRight} className='text-secondary' />
                                        </Col>
                                        <Col xs={5}>
                                            <Badge bg='success' className='w-100 shadow-sm text-wrap'>{change.newData || ''}</Badge>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    )
                })
            }
        </Row >
    );
}

StillageAudit.propTypes = {
    showModal: PropTypes.bool,
    stillageNo: PropTypes.number
};

export default memo(StillageAudit);